import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";
import { IDoesFilterPassParams, IFilterParams } from "ag-grid-community";
import { CheckboxInput } from "./CheckboxInput";
import { ArrayIndicator } from "src/data/services/grid-storage-service";

interface Props extends IFilterParams {
    optionsGenerator?: (field: string, api?: any, colDef?: any) => string[]
}

// Default options generator that extracts unique values from the grid data
// Works with both data and datasource tables, and handles valueGetter columns
const defaultOptionsGenerator = (field: string, api?: any, colDef?: any): string[] => {
    if (!api) {
        return []
    }
    const options: string[] = []
    const seen = new Set<string>()
    const valueGetter = colDef?.valueGetter
    
    api.forEachNode((node) => {
        if (node.data) {
            let value: any
            if (valueGetter) {
                // Use valueGetter if available (for computed values)
                if (typeof valueGetter === "function") {
                    value = valueGetter({ data: node.data, node, api, column: { getColId: () => field } })
                }
            } else {
                // Direct field access
                value = node.data[field]
            }
            if (value != null && value !== "" && !seen.has(String(value))) {
                seen.add(String(value))
                options.push(String(value))
            }
        }
    })
    return options.sort()
}

export const CheckboxSetFilter = forwardRef((props: Props, ref) => {
    const [selections, setSelections] = useState<string[]>([]);
  
    const { field } = props.colDef

    const optionsGenerator = props.optionsGenerator || defaultOptionsGenerator
    
    const options = useMemo( () => 
        optionsGenerator(field, props.api, props.colDef), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [])
  
    // ---- Methods called by framework:
    useImperativeHandle(ref, () => {
        return {
            doesFilterPass(params: IDoesFilterPassParams) {
                const { colDef } = props;
                const { node } = params;
                
                if (selections.length === 0) {
                    return true
                }
                
                // Use valueGetter if available (for computed values), otherwise use direct field access
                // Use props.getValue which handles valueGetter automatically, or fall back to direct field access
                let value: any
                const valueGetter = colDef?.valueGetter
                if (valueGetter && typeof valueGetter === "function") {
                    // Use props.getValue which properly handles valueGetter columns
                    value = props.getValue(node)
                } else {
                    value = node.data[colDef.field]
                }
                
                // Convert value to string for comparison, matching how options are stored
                return selections.includes(String(value))
            },
            isFilterActive() {
                return selections.length > 0;
            },
            getModel() {
                if (!this.isFilterActive()) {
                    return null;
                }
                // Return model compatible with server-side filtering
                // Use ArrayIndicator prefix for array values to trigger "in:" operator
                return { 
                    filterType: "text",
                    type: "in",
                    filter: ArrayIndicator + selections.join(","),
                    value: selections // Keep for backward compatibility
                };
            },
            setModel(model: any) {
                if (model) {
                    // Support both old format ({ value: [...] }) and new format
                    if (model.value) {
                        setSelections(model.value);
                    } else if (model.filter) {
                        // Handle ArrayIndicator format
                        if (String(model.filter).startsWith(ArrayIndicator)) {
                            const filterValue = model.filter.substring(ArrayIndicator.length)
                            setSelections(filterValue ? filterValue.split(",") : [])
                        } else {
                            setSelections([model.filter])
                        }
                    }
                }
            },
        };
    });
    // ---------------------------------


    useEffect(() => {
        props.filterChangedCallback(); // calls the framework methods
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections]);
  
    const onSelect = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        const value = evt.target.value
        const newSelections = evt.target.checked ? 
        [...selections, value] :
        selections.filter(val => val !== value)       
        setSelections(newSelections)
    }

    const areAllSelected = () => {
        return selections.length === options.length
    }

    const onToggleAll = () => {
        const selected = areAllSelected() ? [] : options
        setSelections(selected)
    }

    return (
        <div
            className={field + "-wrapper align-items-center mb-2 outline-FPO"}
            role="region"
            aria-label="checkboxes to filter selections"
            style={{margin: "1rem"}}
        >
            <CheckboxInput
                name={`${field}_SelectAll`}
                key={`${field}_SelectAll`}
                label="Select All"
                checked={areAllSelected()}
                onChange={ () => onToggleAll()}
            />
        <hr></hr>
        
        {options.map(value => (
            <CheckboxInput
                name={`${field}_${value}`}
                key={`${field}_${value}`}
                label={value}
                value={value}
                checked={selections.includes(value)}
                onChange={ (evt) => onSelect(evt)}
            />
        ))}
        </div>
    );
  });
